import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Image,
  Font,
  StyleSheet,
} from '@react-pdf/renderer';
import { DetailedPDFRecord, DetailedReportData } from './detailedTypes';
import { getTypeColor, PDF_COLORS } from './pdfColors';
import { CoverPage } from './components/CoverPage';
import { DataGrid } from './components/DataGrid';
import { PhotoCell } from './components/PhotoCell';
import i18n from '../../i18n';

/** Helper: get translation using current language */
function t(key: string, defaultValue?: string): string {
  return i18n.t(key, { defaultValue: defaultValue ?? key });
}

Font.register({
  family: 'Poppins',
  fonts: [
    { src: '/fonts/Poppins-Regular.ttf' },
    { src: '/fonts/Poppins-SemiBold.ttf', fontWeight: 600 },
  ],
});

const styles = StyleSheet.create({
  page: {
    backgroundColor: PDF_COLORS.white,
    paddingTop: 20,
    paddingBottom: 35,
    paddingHorizontal: 20,
    fontFamily: 'Poppins',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginBottom: 10,
  },
  roidText: {
    fontSize: 11,
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: PDF_COLORS.white,
    letterSpacing: 0.3,
  },
  stateBadge: {
    paddingVertical: 2,
    paddingHorizontal: 7,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.22)',
  },
  stateText: {
    fontSize: 7.5,
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: PDF_COLORS.white,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  sectionTitle: {
    fontSize: 8.5,
    fontFamily: 'Poppins',
    fontWeight: 600,
    marginTop: 9,
    marginBottom: 4,
    paddingBottom: 2,
    borderBottomWidth: 0.5,
    borderBottomColor: PDF_COLORS.greyMid,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  descriptionBlock: {
    marginTop: 6,
    marginBottom: 4,
    padding: 6,
    backgroundColor: PDF_COLORS.greyLight,
    borderRadius: 3,
  },
  descriptionLabel: {
    fontSize: 7,
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: PDF_COLORS.greyDark,
    marginBottom: 2,
  },
  descriptionText: {
    fontSize: 8,
    fontFamily: 'Poppins',
    color: PDF_COLORS.ink,
    lineHeight: 1.45,
  },
  tableContainer: {
    marginTop: 6,
    marginBottom: 4,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
    paddingVertical: 3,
    paddingHorizontal: 3,
  },
  tableHeaderCell: {
    fontSize: 7,
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: PDF_COLORS.white,
    flex: 1,
    paddingHorizontal: 2,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 3,
    paddingHorizontal: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: PDF_COLORS.greyMid,
  },
  tableCell: {
    fontSize: 7,
    fontFamily: 'Poppins',
    color: PDF_COLORS.ink,
    flex: 1,
    paddingHorizontal: 2,
  },
  imagesSection: {
    marginTop: 8,
  },
  imagesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  imageCell: {
    width: 250,
    marginBottom: 6,
    padding: 3,
    borderWidth: 0.5,
    borderColor: PDF_COLORS.greyMid,
    borderRadius: 3,
    backgroundColor: PDF_COLORS.greyLight,
  },
  image: {
    width: '100%',
    objectFit: 'contain',
    borderRadius: 2,
  },
  imageCaption: {
    fontSize: 6.5,
    fontFamily: 'Poppins',
    color: PDF_COLORS.greyDark,
    textAlign: 'center',
    marginTop: 3,
  },
  footer: {
    position: 'absolute',
    bottom: 12,
    left: 20,
    right: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 0.5,
    borderTopColor: PDF_COLORS.greyMid,
    paddingTop: 3,
  },
  footerText: {
    fontSize: 7,
    fontFamily: 'Poppins',
    color: PDF_COLORS.greyDark,
  },
  footerBrand: {
    flex: 1,
    textAlign: 'center',
  },
  damagesBlock: {
    marginTop: 6,
    marginBottom: 4,
    padding: 6,
    backgroundColor: PDF_COLORS.greyLight,
    borderRadius: 3,
  },
  emptyTableText: {
    fontSize: 7,
    fontFamily: 'Poppins',
    color: PDF_COLORS.greyDark,
    textAlign: 'center',
    paddingVertical: 10,
  },
});

/* ------------------------------------------------------------------ */
/*  Reusable sub-components                                           */
/* ------------------------------------------------------------------ */

interface SectionTitleProps {
  title: string;
  typeColor: string;
}

const SectionTitle = ({ title, typeColor }: SectionTitleProps) => (
  <Text style={[styles.sectionTitle, { color: typeColor }]}>{title}</Text>
);

/** Fotos por fila en la ficha (celdas de 250 pt con 8 de separación). */
const PHOTOS_PER_ROW = 2;

/**
 * Fotos o croquis de la ficha.
 *
 * 🔴 El título y la PRIMERA fila van en un bloque que no se parte: cada foto ya
 * es una pieza entera (`PhotoCell`), así que si la primera no cabía al pie de
 * la página pasaba sola a la siguiente y el título («FOTOS») se quedaba
 * huérfano abajo (informe de Su.0000615 en beta, 2026-10-09). El resto de filas
 * fluyen como siempre.
 */
export const PhotoSection = ({
  title,
  typeColor,
  images,
}: {
  title: string;
  typeColor: string;
  images: { url: string; name: string; location?: string[] }[];
}) => {
  const cells = images.map((img, i) => (
    <PhotoCell
      key={i}
      url={img.url}
      name={img.name}
      location={img.location}
      cellStyle={styles.imageCell}
      imageStyle={styles.image}
      captionStyle={styles.imageCaption}
    />
  ));
  return (
    <View style={styles.imagesSection}>
      <View wrap={false}>
        <SectionTitle title={title} typeColor={typeColor} />
        <View style={styles.imagesGrid}>{cells.slice(0, PHOTOS_PER_ROW)}</View>
      </View>
      {cells.length > PHOTOS_PER_ROW && (
        <View style={styles.imagesGrid}>{cells.slice(PHOTOS_PER_ROW)}</View>
      )}
    </View>
  );
};

interface PDFTableProps {
  title: string;
  headers: string[];
  rows: string[][];
  typeColor: string;
  /** Alineación por columna (default 'left'); 'right' para columnas numéricas. */
  align?: ('left' | 'right' | 'center')[];
}

const PDFTable = ({ title, headers, rows, typeColor, align }: PDFTableProps) => {
  if (rows.length === 0) return null;
  const alignOf = (i: number) => align?.[i] ?? 'left';

  return (
    <View style={styles.tableContainer}>
      <SectionTitle title={title} typeColor={typeColor} />
      <View style={[styles.tableHeaderRow, { backgroundColor: typeColor }]}>
        {headers.map((header, i) => (
          <Text
            key={i}
            style={[styles.tableHeaderCell, { textAlign: alignOf(i) }]}
          >
            {header}
          </Text>
        ))}
      </View>
      {rows.map((row, rowIdx) => (
        <View
          key={rowIdx}
          style={[
            styles.tableRow,
            {
              backgroundColor:
                rowIdx % 2 === 0 ? PDF_COLORS.white : PDF_COLORS.greyLight,
            },
          ]}
          wrap={false}
        >
          {row.map((cell, cellIdx) => (
            <Text
              key={cellIdx}
              style={[styles.tableCell, { textAlign: alignOf(cellIdx) }]}
            >
              {cell}
            </Text>
          ))}
        </View>
      ))}
    </View>
  );
};

/* ------------------------------------------------------------------ */
/*  Helper: convert typed arrays into generic header+rows for PDFTable */
/* ------------------------------------------------------------------ */

const buildOperatorsTable = (
  operators: NonNullable<DetailedPDFRecord['operators']>,
): { headers: string[]; rows: string[][] } => ({
  headers: [t('tables:headers.name', 'Nom'), t('tables:headers.surname', 'Cognom'), t('tables:headers.concession')],
  rows: operators.map((o) => [o.name, o.surname, o.concession]),
});

const buildVehiclesTable = (
  vehicles: NonNullable<DetailedPDFRecord['vehicles']>,
): { headers: string[]; rows: string[][] } => ({
  headers: [t('accidents:headers.licensePlate', 'Matrícula'), t('accidents:headers.model', 'Model'), t('forms:vehicle.category', 'Categoria'), t('tables:headers.driver', 'Conductor')],
  rows: vehicles.map((v) => [v.plate, v.model, v.category, v.driver]),
});

const buildMaterialsTable = (
  materials: NonNullable<DetailedPDFRecord['materials']>,
): { headers: string[]; rows: string[][] } => ({
  headers: [t('common:labels.code'), t('tables:headers.description', 'Descripció'), t('tables:headers.ut'), t('tables:headers.typeOfMeasurement'), t('workOrders:quantity')],
  rows: materials.map((m) => [m.code, m.description, m.unitType, m.unit, m.quantity]),
});

const buildMachineryTable = (
  machinery: NonNullable<DetailedPDFRecord['machinery']>,
): { headers: string[]; rows: string[][] } => ({
  headers: [t('common:labels.code'), t('tables:headers.description', 'Descripció'), t('tables:headers.ut'), t('tables:headers.typeOfMeasurement'), t('workOrders:quantity')],
  rows: machinery.map((m) => [m.code, m.description, m.unitType, m.unit, m.quantity]),
});

const buildAnimalsTable = (
  animals: NonNullable<DetailedPDFRecord['animals']>,
): { headers: string[]; rows: string[][] } => ({
  headers: [t('common:labels.code'), t('forms:animals.species'), t('tables:headers.status', 'Estat'), t('workOrders:quantity')],
  rows: animals.map((a) => [a.code, a.species, a.status, a.quantity]),
});

const buildBudgetItemsTable = (
  items: NonNullable<DetailedPDFRecord['budgetItems']>,
): { headers: string[]; rows: string[][] } => ({
  headers: [t('common:labels.code'), t('tables:headers.description', 'Descripció'), t('tables:headers.classification', 'Categoria'), t('workOrders:unit'), t('workOrders:quantity'), t('common:labels.unitPrice'), 'Total'],
  rows: items.map((b) => [
    b.code,
    b.description,
    b.category,
    b.unit,
    b.quantity,
    b.unitPrice,
    b.total,
  ]),
});

const buildWorkVehiclesTable = (
  vehicles: NonNullable<DetailedPDFRecord['workVehicles']>,
): { headers: string[]; rows: string[][] } => ({
  headers: [t('accidents:headers.licensePlate', 'Matrícula'), t('accidents:headers.model', 'Model'), t('forms:vehicle.category', 'Categoria'), t('tables:headers.driver', 'Conductor'), t('forms:vehicle.ownership', 'Propietat')],
  rows: vehicles.map((v) => [v.plate, v.model, v.category, v.driver, v.ownership]),
});

const buildExternalCompaniesTable = (
  companies: NonNullable<DetailedPDFRecord['externalCompanies']>,
): { headers: string[]; rows: string[][] } => ({
  headers: [t('forms:externalCompanies.company', 'Empresa'), t('forms:externalCompanies.operatorsNumberFull', 'Nº OP')],
  rows: companies.map((c) => [c.company, c.workers]),
});

const buildVehiclesInvolvedTable = (
  vehicles: NonNullable<DetailedPDFRecord['vehiclesInvolved']>,
): { headers: string[]; rows: string[][] } => ({
  headers: [
    t('accidents:headers.vehicle', 'Vehicle'),
    t('accidents:headers.licensePlate', 'Matrícula'),
    t('forms:accident.vehicleFields.color', 'Color'),
    t('accidents:headers.model', 'Model'),
    t('accidents:headers.insurance', 'CIA Seg.'),
    t('forms:accident.vehicleFields.policy', 'Núm. Pòlissa'),
    t('accidents:headers.numberOccupants', 'Persones'),
  ],
  rows: vehicles.map((v) => [
    v.vehicle,
    v.plate,
    v.color,
    v.model,
    v.insurance,
    v.policyNumber,
    v.occupants,
  ]),
});

/* ------------------------------------------------------------------ */
/*  RecordSummary — one full page (or more) per record                */
/* ------------------------------------------------------------------ */

interface RecordSummaryProps {
  record: DetailedPDFRecord;
  typeColor: string;
  title: string;
}

export const RecordSummary = ({ record, typeColor, title }: RecordSummaryProps) => (
  <Page size="A4" style={styles.page} wrap={true}>
    {/* Header with ROID */}
    <View style={[styles.header, { backgroundColor: typeColor }]} wrap={false}>
      <Text style={styles.roidText}>{record.roid}</Text>
      {record.state ? (
        <View style={styles.stateBadge}>
          <Text style={styles.stateText}>
            {t(`common:states.${record.state}`, record.state)}
          </Text>
        </View>
      ) : null}
    </View>

    {/* Basic Info */}
    {record.basicInfo.length > 0 && <DataGrid fields={record.basicInfo} />}

    {/* Action extra fields */}
    {record.type === 'action' && (record.activity || record.element || record.asset || record.laneCutting || record.totalHours) && (
      <DataGrid fields={[
        ...(record.activity ? [{ label: t('forms:workOrder.action', 'Activitat'), value: record.activity }] : []),
        ...(record.element ? [{ label: t('forms:workOrder.element', 'Element'), value: record.element }] : []),
        ...(record.asset ? [{ label: t('forms:workOrder.asset', 'Actiu'), value: record.asset }] : []),
        ...(record.laneCutting ? [{ label: t('forms:workOrder.laneCut', 'Tall de carril'), value: record.laneCutting }] : []),
        ...(record.startDate ? [{ label: t('forms:workOrder.startDate', 'Data inici'), value: record.startDate }] : []),
        ...(record.endDate ? [{ label: t('forms:workOrder.endDate', 'Data fi'), value: record.endDate }] : []),
        ...(record.arrivalDate ? [{ label: t('forms:workOrder.arrivalDate', "Data d'arribada"), value: record.arrivalDate }] : []),
        ...(record.totalHours ? [{ label: t('forms:workOrder.totalHours', 'Total hores'), value: record.totalHours }] : []),
        ...(record.totalOperators ? [{ label: t('forms:workOrder.totalOperators', 'Total operaris'), value: record.totalOperators }] : []),
      ]} />
    )}

    {/* Description block */}
    {(record.description || record.notes) && (
      <View style={styles.descriptionBlock} wrap={false}>
        {/* Una sola descripción (2026-10-28): en una actuación, la validada o,
            si no hay, la del operario — nunca las dos, porque el informe se
            entrega a Asistencia. Etiqueta neutra: a veces es la del operario, y
            en accidentes y comunicaciones no hay validada. */}
        {record.description ? (
          <>
            <Text style={styles.descriptionLabel}>{t('common:tables.headers.description', 'Descripció')}</Text>
            <Text style={styles.descriptionText}>{record.description}</Text>
          </>
        ) : null}
        {record.notes ? (
          <>
            <Text
              style={[
                styles.descriptionLabel,
                { marginTop: record.description ? 4 : 0 },
              ]}
            >
              {t('forms:workOrder.notes', 'Notes')}
            </Text>
            <Text style={styles.descriptionText}>{record.notes}</Text>
          </>
        ) : null}
      </View>
    )}

    {/* Tablas genéricas extra (p.ej. tramos del RV) — mismo PDFTable */}
    {record.extraTables?.map((tbl, i) =>
      tbl.rows.length > 0 ? (
        <PDFTable
          key={i}
          title={tbl.title}
          headers={tbl.headers}
          rows={tbl.rows}
          align={tbl.align}
          typeColor={typeColor}
        />
      ) : tbl.emptyText ? (
        <View key={i} style={styles.tableContainer}>
          <SectionTitle title={tbl.title} typeColor={typeColor} />
          <Text style={styles.emptyTableText}>{tbl.emptyText}</Text>
        </View>
      ) : null,
    )}

    {/* External Companies table (action) */}
    {record.externalCompanies && record.externalCompanies.length > 0 && (
      <PDFTable
        title={t('forms:tabs.externalCompanies')}
        {...buildExternalCompaniesTable(record.externalCompanies)}
        typeColor={typeColor}
      />
    )}

    {/* Operators table */}
    {record.operators && record.operators.length > 0 && (
      <PDFTable
        title={t('forms:workOrder.operators', 'Operaris')}
        {...buildOperatorsTable(record.operators)}
        typeColor={typeColor}
      />
    )}

    {/* Vehicles table */}
    {record.vehicles && record.vehicles.length > 0 && (
      <PDFTable
        title={t('forms:workOrder.vehicles', 'Vehicles')}
        {...buildVehiclesTable(record.vehicles)}
        typeColor={typeColor}
      />
    )}

    {/* Hours breakdown (H.P.) */}
    {record.hoursBreakdown && record.hoursBreakdown.length > 0 && (
      <View wrap={false}>
        <SectionTitle title={t('forms:operatorsAndVehicles.personHoursLong', 'Horas por Persona')} typeColor={typeColor} />
        {record.hoursBreakdown.map((item, idx) => (
          <View key={idx} style={{ flexDirection: 'row', paddingVertical: 2, paddingHorizontal: 4, borderBottomWidth: 0.5, borderBottomColor: PDF_COLORS.greyMid }}>
            <Text style={{ fontSize: 7, fontFamily: 'Poppins', fontWeight: item.bold ? 600 : 400, color: item.bold ? PDF_COLORS.ink : PDF_COLORS.greyDark, width: '60%' }}>{item.label}</Text>
            <Text style={{ fontSize: 7, fontFamily: 'Poppins', fontWeight: item.bold ? 600 : 400, color: PDF_COLORS.ink, width: '40%', textAlign: 'right' }}>{item.value}</Text>
          </View>
        ))}
      </View>
    )}

    {/* Materials table */}
    {record.materials && record.materials.length > 0 && (
      <PDFTable
        title={t('forms:workOrder.materials', 'Materials')}
        {...buildMaterialsTable(record.materials)}
        typeColor={typeColor}
      />
    )}

    {/* Machinery table */}
    {record.machinery && record.machinery.length > 0 && (
      <PDFTable
        title={t('forms:workEquipment.auxiliaryMachinery')}
        {...buildMachineryTable(record.machinery)}
        typeColor={typeColor}
      />
    )}

    {/* Work Vehicles table (action - separate from transport) */}
    {record.workVehicles && record.workVehicles.length > 0 && (
      <PDFTable
        title={t('forms:tabs.workVehicles')}
        {...buildWorkVehiclesTable(record.workVehicles)}
        typeColor={typeColor}
      />
    )}

    {/* Animals table */}
    {record.animals && record.animals.length > 0 && (
      <PDFTable
        title={t('common:tabs.animals', 'Animals')}
        {...buildAnimalsTable(record.animals)}
        typeColor={typeColor}
      />
    )}

    {/* Budget Items table */}
    {record.budgetItems && record.budgetItems.length > 0 && (
      <PDFTable
        title={t('workOrders:budgetItemsTab')}
        {...buildBudgetItemsTable(record.budgetItems)}
        align={['left', 'left', 'left', 'left', 'right', 'right', 'right']}
        typeColor={typeColor}
      />
    )}

    {/* Conditions section (accident only) */}
    {record.conditions && record.conditions.length > 0 && (
      <View>
        <SectionTitle title={t('forms:accident.roadCondition', 'Condicions')} typeColor={typeColor} />
        <DataGrid fields={record.conditions} />
      </View>
    )}

    {/* Attention Details (accident only) */}
    {record.attentionDetails && record.attentionDetails.length > 0 && (
      <View>
        <SectionTitle title={t('accidents:accidentAttention', 'Atenció prestada')} typeColor={typeColor} />
        <DataGrid fields={record.attentionDetails} />
      </View>
    )}

    {/* Vehicles Involved table (accident only) */}
    {record.vehiclesInvolved && record.vehiclesInvolved.length > 0 && (
      <PDFTable
        title={t('accidents:vehiclesInvolved', 'Vehicles implicats')}
        {...buildVehiclesInvolvedTable(record.vehiclesInvolved)}
        typeColor={typeColor}
      />
    )}

    {/* Damages block (accident only) */}
    {record.damages && (
      <View style={styles.damagesBlock} wrap={false}>
        <Text style={styles.descriptionLabel}>{t('accidents:damages', 'Danys')}</Text>
        <Text style={styles.descriptionText}>{record.damages}</Text>
      </View>
    )}

    {/* Sketches (action or accident) — rendered in their own section, not mixed with photos */}
    {record.sketches && record.sketches.length > 0 && (
      <PhotoSection
        title={t('forms:actionEdition.sketch', 'Croquis')}
        typeColor={typeColor}
        images={record.sketches}
      />
    )}

    {/* Images grid — photos only (sketches are rendered above in their own section), 2 per row */}
    {record.images.length > 0 && (
      <PhotoSection
        title={t('forms:actionEdition.photos', 'Imatges')}
        typeColor={typeColor}
        images={record.images}
      />
    )}

    {/* Pie de página. Sin «página X / Y»: retirado a petición del cliente
        (2026-08-04). El hueco se conserva para que la marca siga centrada. */}
    <View style={styles.footer} fixed>
      <Text style={styles.footerText}>{title}</Text>
      <Text style={[styles.footerText, styles.footerBrand]}>
        UTE Eix Transversal C-25 · Generalitat de Catalunya
      </Text>
      <Text style={styles.footerText} />
    </View>
  </Page>
);

/* ------------------------------------------------------------------ */
/*  DetailedReport — main document                                    */
/* ------------------------------------------------------------------ */

interface DetailedReportProps {
  data: DetailedReportData;
}

export const DetailedReport = ({ data }: DetailedReportProps) => {
  const typeColor = getTypeColor(data.orderType);

  return (
    <Document>
      <CoverPage
        title={data.title}
        typeColor={typeColor}
        generatedAt={data.generatedAt}
        totalCount={data.totalCount}
        filtersSummary={data.filtersSummary}
      />
      {data.records.map((record, i) => (
        <RecordSummary
          key={i}
          record={record}
          typeColor={typeColor}
          title={data.title}
        />
      ))}
    </Document>
  );
};
